if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}
/**
 *  @class
 *  @function ThemeHeader
 */
if (!customElements.get('theme-header')) {
  class ThemeHeader extends HTMLElement {
    constructor() {
      super();
      this.handleSetStickyClass = this.setStickyClass.bind(this);
      this.handleMeasurements = this.updateMeasurements.bind(this);
      this.handleSectionLifecycle = () => {
        this.refreshMeasurementObserver();
        this.updateMeasurements();
      };
    }
    connectedCallback() {
      this.header_section = document.querySelector('.header-section');
      this.menu = this.querySelector('#mobile-menu');
      this.toggle = document.querySelector('.mobile-toggle-wrapper');

      document.addEventListener('keyup', (e) => {
        if (e.code) {
          if (e.code.toUpperCase() === 'ESCAPE') {
            this.toggle.removeAttribute('open');
            this.toggle.classList.remove('active');
          }
        }
      });
      this.toggle.querySelector('.mobile-toggle').addEventListener('click', (e) => {
        if (this.toggle.classList.contains('active')) {
          e.preventDefault();
          document.body.classList.remove('overflow-hidden');
          this.toggle.classList.remove('active');
          this.closeAnimation(this.toggle);
        } else {
          document.body.classList.add('overflow-hidden');
          setTimeout(() => {
            this.toggle.classList.add('active');
          });
        }
        window.dispatchEvent(new Event('resize.resize-select'));
      });

      // Sticky Header Class
      window.addEventListener('scroll', this.handleSetStickyClass, {
        passive: true
      });
      window.addEventListener('scroll', this.handleMeasurements, {
        passive: true
      });
      window.addEventListener('resize', this.handleMeasurements, {
        passive: true
      });
      window.addEventListener('load', this.handleMeasurements);
      document.addEventListener('shopify:section:load', this.handleSectionLifecycle);
      document.addEventListener('shopify:section:unload', this.handleSectionLifecycle);

      this.refreshMeasurementObserver();

      this.setStickyClass();
      this.updateMeasurements();

      // Buttons.
      this.menu.querySelectorAll('summary').forEach(summary => summary.addEventListener('click', this.onSummaryClick.bind(this)));
      this.menu.querySelectorAll('.parent-link-back--button').forEach(button => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleSetStickyClass);
      window.removeEventListener('scroll', this.handleMeasurements);
      window.removeEventListener('resize', this.handleMeasurements);
      window.removeEventListener('load', this.handleMeasurements);
      document.removeEventListener('shopify:section:load', this.handleSectionLifecycle);
      document.removeEventListener('shopify:section:unload', this.handleSectionLifecycle);

      if (this.measurementObserver) {
        this.measurementObserver.disconnect();
        this.measurementObserver = null;
      }
    }
    getAnnouncementSections() {
      return Array.from(document.querySelectorAll('.announcement-bar-section'));
    }
    getElementHeight(element) {
      if (!element) {
        return 0;
      }

      const styles = window.getComputedStyle(element);
      if (styles.display === 'none') {
        return 0;
      }

      return Math.round(element.getBoundingClientRect().height);
    }
    updateMeasurements() {
      this.setHeaderOffset();
      this.setHeaderHeight();
      this.setAnnouncementHeight();
    }
    refreshMeasurementObserver() {
      if (typeof ResizeObserver === 'undefined') {
        return;
      }

      if (this.measurementObserver) {
        this.measurementObserver.disconnect();
      }

      this.measurementObserver = new ResizeObserver(() => this.updateMeasurements());

      [this, this.header_section, ...this.getAnnouncementSections()].forEach((element) => {
        if (element) {
          this.measurementObserver.observe(element);
        }
      });
    }

    setStickyClass() {
      if (this.classList.contains('header-sticky--active')) {
        let offset = parseInt(this.getBoundingClientRect().top, 10) + document.documentElement.scrollTop;
        this.classList.toggle('is-sticky', window.scrollY >= offset && window.scrollY > 0);
      }
    }
    setAnnouncementHeight() {
      const height = this.getAnnouncementSections().reduce((total, element) => {
        return total + this.getElementHeight(element);
      }, 0);

      document.documentElement.style.setProperty('--announcement-height', `${height}px`);
    }
    setHeaderOffset() {
      const h = this.header_section ? Math.max(this.header_section.getBoundingClientRect().top, 0) : 0;
      document.documentElement.style.setProperty('--header-offset', `${h}px`);
    }
    setHeaderHeight() {
      const h = this.getElementHeight(this);
      document.documentElement.style.setProperty('--header-height', `${h}px`);
    }
    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.link-container');
      const isOpen = detailsElement.hasAttribute('open');

      if (this.querySelector('.parent-link-back--button')) {
        this.menu.scrollTop = 0;
      }

      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
      }, 100);
    }
    onCloseButtonClick(event) {
      event.preventDefault();
      const detailsElement = event.currentTarget.closest('details');
      this.closeSubmenu(detailsElement);
    }
    closeSubmenu(detailsElement) {
      detailsElement.classList.remove('menu-opening');
      this.closeAnimation(detailsElement);
    }
    closeAnimation(detailsElement) {
      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }
  customElements.define('theme-header', ThemeHeader);
}
/**
 *  @class
 *  @function FullMenu
 */
if (!customElements.get('full-menu')) {
  class FullMenu extends HTMLElement {
    constructor() {
      super();
    }
    connectedCallback() {
      this.submenus = this.querySelectorAll('.thb-full-menu>.menu-item-has-children:not(.menu-item-has-megamenu)>.sub-menu');

      if (!this.submenus.length) {
        return;
      }
      const _this = this;
      // resize on initial load
      window.addEventListener('resize', debounce(function () {
        _this.resizeSubMenus();
      }, 100));

      window.dispatchEvent(new Event('resize'));

      document.fonts.ready.then(function () {
        _this.resizeSubMenus();
      });

    }
    resizeSubMenus() {
      this.submenus.forEach((submenu) => {
        let sub_submenus = submenu.querySelectorAll(':scope >.menu-item-has-children>.sub-menu');

        sub_submenus.forEach((sub_submenu) => {
          let w = sub_submenu.offsetWidth,
            l = sub_submenu.parentElement.parentElement.getBoundingClientRect().left + sub_submenu.parentElement.parentElement.clientWidth + 10,
            total = w + l;
          if (total > document.body.clientWidth) {
            sub_submenu.parentElement.classList.add('left-submenu');
          } else if (sub_submenu.parentElement.classList.contains('left-submenu')) {
            sub_submenu.parentElement.classList.remove('left-submenu');
          }
        });
      });
    }
  }
  customElements.define('full-menu', FullMenu);
}
